// utils/crypto.js
// Client-side encryption utilities using Web Crypto API

/**
 * Generate a random salt
 */
export const generateSalt = () => {
  const salt = window.crypto.getRandomValues(new Uint8Array(32));
  return arrayBufferToBase64(salt);
};

/**
 * Generate a random recovery key (256-bit)
 */
export const generateRecoveryKey = () => {
  const recoveryKey = window.crypto.getRandomValues(new Uint8Array(32));
  return arrayBufferToBase64(recoveryKey);
};

/**
 * Derive a symmetric key from password using PBKDF2
 * (Note: For production, consider using Argon2 via a WASM library like argon2-browser)
 */
export const deriveKeyFromPassword = async (password, salt) => {
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);
  const saltBuffer = base64ToArrayBuffer(salt);

  // Import password as key material
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  // Derive AES-GCM key
  const derivedKey = await window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: 100000, // Adjust for security vs performance
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );

  return derivedKey;
};

/**
 * Derive a symmetric key from recovery key
 */
export const deriveKeyFromRecovery = async (recoveryKey, salt) => {
  const recoveryKeyBuffer = base64ToArrayBuffer(recoveryKey);
  const saltBuffer = base64ToArrayBuffer(salt);

  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    recoveryKeyBuffer,
    'PBKDF2',
    false,
    ['deriveKey']
  );

  const derivedKey = await window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );

  return derivedKey;
};

/**
 * Encrypt data with AES-GCM
 */
export const encryptWithSymmetricKey = async (data, key) => {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));

  const encryptedData = await window.crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv
    },
    key,
    dataBuffer
  );

  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + encryptedData.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encryptedData), iv.length);

  return arrayBufferToBase64(combined);
};

/**
 * Decrypt data with AES-GCM
 */
export const decryptWithSymmetricKey = async (encryptedData, key) => {
  const combined = base64ToArrayBuffer(encryptedData);
  const iv = combined.slice(0, 12);
  const data = combined.slice(12);

  const decryptedData = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: iv
    },
    key,
    data
  );

  const decoder = new TextDecoder();
  return decoder.decode(decryptedData);
};

/**
 * Generate RSA key pair for asymmetric encryption
 */
export const generateKeyPair = async () => {
  const keyPair = await window.crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256'
    },
    true,
    ['encrypt', 'decrypt']
  );

  // Export keys
  const publicKey = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);
  const privateKey = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);

  return {
    publicKey: arrayBufferToBase64(publicKey),
    privateKey: arrayBufferToBase64(privateKey)
  };
};

/**
 * Hash recovery key for storage (SHA-256)
 */
export const hashRecoveryKey = async (recoveryKey) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(recoveryKey);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  return arrayBufferToBase64(hashBuffer);
};

/**
 * Export symmetric key as raw bytes
 */
export const exportSymmetricKey = async (key) => {
  const exported = await window.crypto.subtle.exportKey('raw', key);
  return arrayBufferToBase64(exported);
};

/**
 * Import symmetric key from raw bytes
 */
export const importSymmetricKey = async (keyData) => {
  const keyBuffer = base64ToArrayBuffer(keyData);
  return await window.crypto.subtle.importKey(
    'raw',
    keyBuffer,
    'AES-GCM',
    true,
    ['encrypt', 'decrypt']
  );
};

// Helper functions
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

const base64ToArrayBuffer = (base64) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

/**
 * Complete registration crypto flow
 */
export const prepareRegistrationData = async (email, password, projectId) => {
  // 1. Generate salt
  const salt = generateSalt();

  // 2. Generate recovery key
  const recoveryKey = generateRecoveryKey();

  // 3. Derive symmetric key from password
  const symmetricKey = await deriveKeyFromPassword(password, salt);

  // 4. Generate RSA key pair
  const { publicKey, privateKey } = await generateKeyPair();

  // 5. Encrypt private key with symmetric key
  const encryptedPrivateKey = await encryptWithSymmetricKey(privateKey, symmetricKey);

  // 6. Export symmetric key and encrypt it with itself (for storage)
  const symmetricKeyRaw = await exportSymmetricKey(symmetricKey);
  const encryptedSymmetricKey = await encryptWithSymmetricKey(symmetricKeyRaw, symmetricKey);

  // 7. Derive key from recovery key and encrypt symmetric key with it
  const recoveryDerivedKey = await deriveKeyFromRecovery(recoveryKey, salt);
  const encryptedSymmetricKeyWithRecovery = await encryptWithSymmetricKey(
    symmetricKeyRaw,
    recoveryDerivedKey
  );

  // 8. Hash recovery key for verification
  const recoveryKeyHash = await hashRecoveryKey(recoveryKey);

  // IMPORTANT: Return snake_case field names to match backend expectations
  return {
    email,
    password, // Will be hashed on backend
    public_key: publicKey,  // ✓ Changed from publicKey
    encrypted_private_key: encryptedPrivateKey,  // ✓ Changed from encryptedPrivateKey
    encrypted_symmetric_key: encryptedSymmetricKey,  // ✓ Changed from encryptedSymmetricKey
    salt,
    recoveryKey, // Show this to user ONCE (not sent to backend)
    recovery_key_hash: recoveryKeyHash,  // ✓ Changed from recoveryKeyHash
    encrypted_symmetric_key_with_recovery: encryptedSymmetricKeyWithRecovery,  // ✓ Changed from encryptedSymmetricKeyWithRecovery
    project_id: projectId  // ✓ Changed from projectId
  };
};

/**
 * Login crypto flow - decrypt keys
 */
export const decryptUserKeys = async (password, encryptionData) => {
  const { salt, encrypted_symmetric_key, encrypted_private_key } = encryptionData;

  // 1. Derive symmetric key from password
  const symmetricKey = await deriveKeyFromPassword(password, salt);

  // 2. Decrypt the symmetric key (verify password is correct)
  try {
    const symmetricKeyRaw = await decryptWithSymmetricKey(encrypted_symmetric_key, symmetricKey);
    const decryptedSymmetricKey = await importSymmetricKey(symmetricKeyRaw);

    // 3. Decrypt private key
    const privateKey = await decryptWithSymmetricKey(encrypted_private_key, decryptedSymmetricKey);

    return {
      symmetricKey: decryptedSymmetricKey,
      privateKey,
      success: true
    };
  } catch (error) {
    return {
      success: false,
      error: 'Invalid password'
    };
  }
};

// Replace your resetPasswordWithRecovery function with this:

export const resetPasswordWithRecovery = async (recoveryKey, newPassword, encryptionData) => {
  const { salt, encrypted_symmetric_key_with_recovery, encrypted_private_key } = encryptionData;

  console.log('🔐 Starting password reset with recovery key');
  console.log('Input data:', {
    oldSalt: salt?.substring(0, 20) + '...',
    hasEncryptedSymKeyWithRecovery: !!encrypted_symmetric_key_with_recovery,
    hasEncryptedPrivKey: !!encrypted_private_key
  });

  try {
    // 1. Derive key from recovery key using OLD salt
    console.log('Step 1: Deriving key from recovery key with old salt...');
    const recoveryDerivedKey = await deriveKeyFromRecovery(recoveryKey, salt);

    // 2. Decrypt symmetric key using recovery key
    console.log('Step 2: Decrypting symmetric key with recovery key...');
    const symmetricKeyRaw = await decryptWithSymmetricKey(
      encrypted_symmetric_key_with_recovery,
      recoveryDerivedKey
    );
    
    // Re-import the symmetric key so we can use it
    const symmetricKey = await importSymmetricKey(symmetricKeyRaw);
    console.log('✓ Symmetric key decrypted and imported');

    // 3. Decrypt private key with symmetric key
    console.log('Step 3: Decrypting private key...');
    const privateKeyData = await decryptWithSymmetricKey(encrypted_private_key, symmetricKey);
    console.log('✓ Private key decrypted');

    // 4. Generate NEW salt for the new password
    console.log('Step 4: Generating new salt...');
    const newSalt = generateSalt();
    console.log('✓ New salt generated:', newSalt.substring(0, 20) + '...');

    // 5. Generate NEW recovery key
    console.log('Step 5: Generating new recovery key...');
    const newRecoveryKey = generateRecoveryKey();
    console.log('✓ New recovery key generated');

    // 6. Derive new key from NEW password with NEW salt
    console.log('Step 6: Deriving key from new password with new salt...');
    const newPasswordDerivedKey = await deriveKeyFromPassword(newPassword, newSalt);
    console.log('✓ New password-derived key created');
    
    // 7. Derive key from NEW recovery key with NEW salt
    console.log('Step 7: Deriving key from new recovery key with new salt...');
    const newRecoveryDerivedKey = await deriveKeyFromRecovery(newRecoveryKey, newSalt);
    console.log('✓ New recovery-derived key created');
    
    // 8. Re-encrypt the SAME symmetric key with NEW password-derived key
    console.log('Step 8: Re-encrypting symmetric key with new password...');
    const newEncryptedSymmetricKey = await encryptWithSymmetricKey(
      symmetricKeyRaw,  // Same symmetric key raw data
      newPasswordDerivedKey
    );
    console.log('✓ Symmetric key re-encrypted with new password');
    
    // 9. Re-encrypt the SAME symmetric key with NEW recovery key
    console.log('Step 9: Re-encrypting symmetric key with new recovery key...');
    const newEncryptedSymmetricKeyWithRecovery = await encryptWithSymmetricKey(
      symmetricKeyRaw,  // Same symmetric key raw data
      newRecoveryDerivedKey
    );
    console.log('✓ Symmetric key re-encrypted with new recovery key');
    
    // 10. Re-encrypt private key with the SAME symmetric key (but new IV)
    console.log('Step 10: Re-encrypting private key with symmetric key...');
    const newEncryptedPrivateKey = await encryptWithSymmetricKey(
      privateKeyData,  // Same private key data
      symmetricKey     // Same symmetric key
    );
    console.log('✓ Private key re-encrypted');
    
    // 11. Hash the new recovery key for storage
    console.log('Step 11: Hashing new recovery key...');
    const newRecoveryKeyHash = await hashRecoveryKey(newRecoveryKey);
    console.log('✓ New recovery key hashed');

    console.log('🎉 Password reset successful!');
    console.log('Summary:', {
      newSalt: newSalt.substring(0, 20) + '...',
      hasNewEncryptedSymKey: !!newEncryptedSymmetricKey,
      hasNewEncryptedPrivKey: !!newEncryptedPrivateKey,
      hasNewRecoveryKey: !!newRecoveryKey,
      hasNewRecoveryKeyHash: !!newRecoveryKeyHash
    });

    return {
      success: true,
      encrypted_private_key: newEncryptedPrivateKey,
      encrypted_symmetric_key: newEncryptedSymmetricKey,
      encrypted_symmetric_key_with_recovery: newEncryptedSymmetricKeyWithRecovery,
      recovery_key_hash: newRecoveryKeyHash,
      salt: newSalt,
      newRecoveryKey: newRecoveryKey // Return this to show the user ONCE
    };

  } catch (error) {
    console.error('❌ Password reset failed:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack
    });
    
    return {
      success: false,
      error: error.message === 'Unsupported state or unable to authenticate data' 
        ? 'Invalid recovery key' 
        : error.message || 'Invalid recovery key'
    };
  }
};